/**
 * Step 2: Field Mapping
 *
 * Smart field mapping with auto-detection and localStorage persistence.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { canUseStorage } from '../../../hooks/useCookieConsent';
import { Button } from '../../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Alert, AlertDescription } from '../../ui/alert';
import { ArrowRight, CheckCircle2, AlertCircle, RotateCcw, X } from 'lucide-react';

// Database field definitions with Irish/English variations
const DATABASE_FIELDS = [
  { key: 'first_name', variations: ['first name', 'first_name', 'firstname', 'forename', 'ainm', 'ainm baiste'] },
  { key: 'last_name', variations: ['last name', 'last_name', 'lastname', 'surname', 'sloinne'] },
  { key: 'birthday', variations: ['birthday', 'dob', 'date of birth', 'birth_date', 'breithlá', 'dáta breithe'] },
  { key: 'gender', variations: ['gender', 'sex', 'inscne', 'cailín', 'buachaill', 'gnéas'] },
  { key: 'school', variations: ['school', 'scoil'] },
  { key: 'school_year', variations: ['school year', 'school_year', 'current school year', 'year', 'bliain'] },
  { key: 'home_county', variations: ['county', 'home county', 'home_county', 'contae'] },
  { key: 'attended_before', variations: ['attended before', 'attended_before', 'previous', 'previously attended', 'ar fhreastail', 'dfhreastail'] },
  { key: 'when_attended', variations: ['when attended', 'when_attended', 'previous year', 'latest year', 'bhliain'] },
  { key: 'address_1', variations: ['address', 'address 1', 'address_1', 'street', 'seoladh', 'seoladh 1'] },
  { key: 'address_2', variations: ['address 2', 'address_2', 'address line 2', 'seoladh 2'] },
  { key: 'address_3', variations: ['address 3', 'address_3', 'address line 3', 'seoladh 3'] },
  { key: 'address_4', variations: ['address 4', 'address_4', 'address line 4', 'seoladh 4'] },
  { key: 'eircode', variations: ['eircode', 'postcode', 'postal code', 'zip'] },
  { key: 'parent_name', variations: ['parent name', 'parent_name', 'guardian', 'tuismitheoir'] },
  { key: 'parent_email', variations: ['parent email', 'parent_email', 'email', 'parental', 'guardian email', 'ríomhphost'] },
  { key: 'parent_phone', variations: ['parent phone', 'parent_phone', 'phone', 'mobile', 'parental', 'guardian phone', 'contact', 'fón'] },
  { key: 'travel_required', variations: ['travel', 'travel required', 'travel_required', 'return travel', 'taisteal'] },
  { key: 'travel_location', variations: ['travel location', 'travel_location', 'pickup', 'location', 'choose location', 'áit'] },
  { key: 'course', variations: ['course', 'cúrsa', 'session', 'roghnaigh', 'choose course'] },
  { key: 'is_cinnire', variations: ['cinnire', 'is_cinnire', 'is cinnire', 'leader', 'is leader'] },
  { key: 'payment_amount', variations: ['amount', 'payment', 'payment_amount', 'fee', 'táille'] },
  { key: 'payment_date', variations: ['payment date', 'payment_date', 'paid date'] },
  { key: 'payment_status', variations: ['payment status', 'payment_status', 'paid'] },
  { key: 'transaction_id', variations: ['transaction', 'transaction_id', 'reference'] },
  { key: 'notes', variations: ['notes', 'comments', 'nótaí'] },
];

const REQUIRED_FIELDS = [
  'first_name',
  'last_name',
  'birthday',
  'gender',
  'parent_email',
  'parent_phone',
  'course',
  'school_year',
  'address_1',
  'home_county',
];

const STORAGE_KEY = 'bulk_upload_field_mapping';

export default function FieldMappingStep({ csvData, fieldMapping, onNext, onBack, onMappingChange }) {
  const { t } = useTranslation();
  const [mapping, setMapping] = useState({});
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    // Initialize with auto-detected or saved mapping
    if (fieldMapping) {
      setMapping(fieldMapping);
    } else {
      const autoMapping = autoDetectMapping(csvData.columns);
      setMapping(autoMapping);
      onMappingChange(autoMapping);
    }
  }, [csvData, fieldMapping]);

  const autoDetectMapping = (csvColumns) => {
    const detected = {};

    csvColumns.forEach(csvCol => {
      const normalized = csvCol.toLowerCase().trim();

      // Find best match
      for (const field of DATABASE_FIELDS) {
        if (field.variations.some(variation => normalized.includes(variation.toLowerCase()))) {
          detected[csvCol] = field.key;
          break;
        }
      }
    });

    // Try to load from localStorage
    const saved = loadMappingFromStorage();
    if (saved) {
      // Merge saved with auto-detected (prefer saved for matching columns)
      csvColumns.forEach(csvCol => {
        if (saved[csvCol]) {
          detected[csvCol] = saved[csvCol];
        }
      });
    }

    return detected;
  };

  const loadMappingFromStorage = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const saveMappingToStorage = (mappingToSave) => {
    if (!canUseStorage()) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mappingToSave));
    } catch {
      // Ignore storage errors
    }
  };

  const handleMappingChange = (csvColumn, dbField) => {
    const newMapping = { ...mapping };

    if (dbField === 'IGNORE') {
      delete newMapping[csvColumn];
    } else {
      newMapping[csvColumn] = dbField;
    }

    setMapping(newMapping);
    onMappingChange(newMapping);
    saveMappingToStorage(newMapping);
  };

  const handleReset = () => {
    const autoMapping = autoDetectMapping(csvData.columns);
    setMapping(autoMapping);
    onMappingChange(autoMapping);
  };

  const validateMapping = () => {
    const validationErrors = [];
    const mappedFields = Object.values(mapping);

    // Check required fields
    REQUIRED_FIELDS.forEach(field => {
      if (!mappedFields.includes(field)) {
        validationErrors.push({
          field,
          message: t('bulkUpload.errors.requiredFieldMissing', {
            field: t(`bulkUpload.fields.${field}`)
          })
        });
      }
    });

    // Check for duplicate mappings
    const duplicates = mappedFields.filter((field, idx) =>
      mappedFields.indexOf(field) !== idx
    );

    if (duplicates.length > 0) {
      duplicates.forEach(field => {
        validationErrors.push({
          field,
          message: t('bulkUpload.errors.duplicateMapping', {
            field: t(`bulkUpload.fields.${field}`)
          })
        });
      });
    }

    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  const duplicateDbFields = useMemo(() => {
    const fields = Object.values(mapping);
    const seen = new Set();
    const dupes = new Set();
    fields.forEach(f => (seen.has(f) ? dupes.add(f) : seen.add(f)));
    return dupes;
  }, [mapping]);

  const handleNext = () => {
    if (validateMapping()) {
      onNext();
    }
  };

  const getMappedCount = () => {
    return Object.keys(mapping).length;
  };

  const getRequiredMappedCount = () => {
    const mappedFields = Object.values(mapping);
    return REQUIRED_FIELDS.filter(field => mappedFields.includes(field)).length;
  };

  // Check if all required fields are mapped
  const allRequiredFieldsMapped = () => {
    const mappedFields = Object.values(mapping);
    return REQUIRED_FIELDS.every(field => mappedFields.includes(field));
  };

  // Get list of unmapped required fields
  const getUnmappedRequiredFields = () => {
    const mappedFields = Object.values(mapping);
    return REQUIRED_FIELDS.filter(field => !mappedFields.includes(field));
  };

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg flex-shrink-0">
        <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
          {t('bulkUpload.steps.mapping.instructions.title')}
        </h3>
        <p className="text-sm text-blue-800 dark:text-blue-200">
          {t('bulkUpload.steps.mapping.instructions.description')}
        </p>
      </div>

      {/* Progress Stats - Fixed Outside Scrolling Area */}
      <div className="grid grid-cols-3 gap-4 flex-shrink-0">
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{csvData.columns.length}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {t('bulkUpload.steps.mapping.stats.totalColumns')}
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{getMappedCount()}</div>
          <div className="text-sm text-blue-700 dark:text-blue-300">
            {t('bulkUpload.steps.mapping.stats.mapped')}
          </div>
        </div>

        <div className={`p-4 rounded-lg ${allRequiredFieldsMapped() ? 'bg-green-50 dark:bg-green-950/30' : 'bg-red-50 dark:bg-red-950/30'}`}>
          <div className={`text-2xl font-bold ${allRequiredFieldsMapped() ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
            {getRequiredMappedCount()}/{REQUIRED_FIELDS.length}
          </div>
          <div className={`text-sm ${allRequiredFieldsMapped() ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
            {t('bulkUpload.steps.mapping.stats.required')}
          </div>
          {!allRequiredFieldsMapped() && (
            <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-800">
              <div className="text-xs font-semibold text-red-900 dark:text-red-100 mb-1">
                {t('bulkUpload.steps.mapping.missingRequired.title', {
                  count: getUnmappedRequiredFields().length
                })}
              </div>
              <ul className="text-xs text-red-800 dark:text-red-200 space-y-0.5">
                {getUnmappedRequiredFields().map((field) => (
                  <li key={field}>• {t(`bulkUpload.fields.${field}`)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Mapping Table */}
      <div className="border rounded-lg overflow-hidden flex-1 flex flex-col min-h-0">
        <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 border-b flex justify-between items-center flex-shrink-0">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            {t('bulkUpload.steps.mapping.table.title')}
          </h3>
          <Button variant="outline" size="sm" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            {t('bulkUpload.steps.mapping.resetButton')}
          </Button>
        </div>

        <div className="divide-y overflow-y-auto min-h-0">
          {csvData.columns.map((csvCol, idx) => {
            const mappedField = mapping[csvCol];
            const isRequired = REQUIRED_FIELDS.includes(mappedField);
            const isDuplicate = mappedField && duplicateDbFields.has(mappedField);

            return (
              <div
                key={idx}
                className={`flex items-center px-4 py-3 ${
                  isDuplicate
                    ? 'bg-amber-50 dark:bg-amber-950/30'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/30'
                }`}
              >
                {/* CSV Column */}
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{csvCol}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t('bulkUpload.steps.mapping.table.csvColumn')}
                  </div>
                </div>

                {/* Arrow */}
                <ArrowRight className="w-5 h-5 text-gray-400 dark:text-gray-500 mx-4" />

                {/* Database Field Selector */}
                <div className="flex-1 flex items-center gap-2">
                  <Select
                    value={mappedField || 'IGNORE'}
                    onValueChange={(value) => handleMappingChange(csvCol, value)}
                  >
                    <SelectTrigger
                      className={
                        isDuplicate
                          ? 'border-amber-500'
                          : isRequired
                          ? 'border-green-500'
                          : ''
                      }
                    >
                      <SelectValue placeholder={t('bulkUpload.steps.mapping.table.selectField')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="IGNORE">
                        {t('bulkUpload.steps.mapping.table.ignore')}
                      </SelectItem>
                      {DATABASE_FIELDS.map(field => (
                        <SelectItem key={field.key} value={field.key}>
                          {t(`bulkUpload.fields.${field.key}`)}
                          {REQUIRED_FIELDS.includes(field.key) && ' *'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Quick Ignore Button */}
                  {mappedField && mappedField !== 'IGNORE' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      onClick={() => handleMappingChange(csvCol, 'IGNORE')}
                      title={t('bulkUpload.steps.mapping.table.ignore')}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Status Indicator */}
                <div className="ml-4 w-6">
                  {isDuplicate ? (
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                  ) : isRequired ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Validation Errors */}
      {errors.length > 0 && (
        <Alert variant="destructive" className="flex-shrink-0">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-semibold mb-2">
              {t('bulkUpload.steps.mapping.validationErrors')}:
            </div>
            <ul className="list-disc list-inside space-y-1">
              {errors.map((error, idx) => (
                <li key={idx}>{error.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
